import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

class PDFTemplateService {
  constructor() {
    this.templateUrl = '/templates/fahrtennachweis_rote_kennzeichen.pdf';
  }

  // Load the PDF template
  async loadTemplate() {
    try {
      const response = await fetch(this.templateUrl);
      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.statusText}`);
      }
      const templateBytes = await response.arrayBuffer();
      return await PDFDocument.load(templateBytes);
    } catch (error) {
      console.error('Error loading PDF template:', error);
      throw error;
    }
  }

  // Populate the official template with fahrten data
  async populateTemplate(fahrten, options = {}) {
    try {
      const pdfDoc = await this.loadTemplate();
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const { width, height } = firstPage.getSize();

      // Get the default font
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // Helper function to format date
      const formatDayMonth = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
      };

      const formatYear = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.getFullYear().toString();
      };

      // Helper function to truncate text to fit in fields
      const truncateText = (text, maxLength = 15) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) : text;
      };

      // Function to get the display label for fahrtzweck
      const getFahrtzweckLabel = (value) => {
        const fahrtzweckOptions = {
          'innen-aussenreinigung': 'Innen und aussenreinigung',
          'zurueck-gefahren-fertig-sauber': 'Zurück gefahren fertig mit Sauber machen',
          'abholung-reinigung': 'Abholung für Reinigung',
          'zurueck-gefahren-fertig-pkw': 'Zurück gefahren fertiger PKW'
        };
        return fahrtzweckOptions[value] || value || '';
      };

      // NOTE: You need to adjust these coordinates based on your official PDF form
      // These are example coordinates - you'll need to find the exact positions
      // by examining your official PDF form
      
      // Define field positions for the official form
      // You can find these coordinates by:
      // 1. Opening the PDF in a PDF editor
      // 2. Measuring the exact positions of each field
      // 3. Updating these coordinates accordingly
      
      // Updated exact coordinates from your PDF template
      // Row spacing: 28 points (504 - 476 = 28)
      const rowSpacing = 61;
      
      // License plate position (adjust coordinates based on your template)
      const licensePlatePosition = { x: 569, y: 576 }; // Adjust these coordinates
      
      const fieldPositions = {
        // Row 1 (first fahrt - first sub-row)
        row1: {
          dateDayMonth: { x: 22, y: 504 },   // Tag Monat
          timeStart: { x: 90, y: 504 },     // Beginn
          vehicleClass: { x: 142, y: 504 },  // Fzg.klasse
          vehicleMake: { x: 142, y: 475 },   // Hersteller
          startziel: { x: 365, y: 504 },     // Startziel
          fahrtzweck: { x: 500, y: 504 },    // Zweck der Fahrt
          fahrerName: { x: 595, y: 503 },    // Nachname vorname
        },
        // Row 1b (first fahrt - second sub-row)
        row1b: {
          dateYear: { x: 22, y: 476 },       // jahr
          timeEnd: { x: 90, y: 476 },       // Ende
          vehicleVin: { x: 237, y: 492 },    // VIN
          ueber: { x: 436, y: 504 },         // Über
          endziel: { x: 365, y: 474 },       // Endziel
          fahrerAdresse: { x: 595, y: 473 }, // plz ort Straße
        },
        // Row 2 (second fahrt - first sub-row)
        row2: {
          dateDayMonth: { x: 22, y: 504 - rowSpacing },
          timeStart: { x: 90, y: 504 - rowSpacing },
          vehicleClass: { x: 142, y: 504 - rowSpacing },
          vehicleMake: { x: 142, y: 475 - rowSpacing },
          startziel: { x: 365, y: 504 - rowSpacing },
          fahrtzweck: { x: 500, y: 504 - rowSpacing },
          fahrerName: { x: 595, y: 503 - rowSpacing },
        },
        // Row 2b (second fahrt - second sub-row)
        row2b: {
          dateYear: { x: 22, y: 476 - rowSpacing },
          timeEnd: { x: 90, y: 476 - rowSpacing },
          vehicleVin: { x: 237, y: 492 - rowSpacing },
          ueber: { x: 436, y: 504 - rowSpacing },
          endziel: { x: 365, y: 474 - rowSpacing },
          fahrerAdresse: { x: 595, y: 473 - rowSpacing },
        },
        // Row 3 (third fahrt - first sub-row)
        row3: {
          dateDayMonth: { x: 22, y: 504 - (rowSpacing * 2) },
          timeStart: { x: 90, y: 504 - (rowSpacing * 2) },
          vehicleClass: { x: 142, y: 504 - (rowSpacing * 2) },
          vehicleMake: { x: 142, y: 475 - (rowSpacing * 2) },
          startziel: { x: 365, y: 504 - (rowSpacing * 2) },
          fahrtzweck: { x: 500, y: 504 - (rowSpacing * 2) },
          fahrerName: { x: 595, y: 503 - (rowSpacing * 2) },
        },
        // Row 3b (third fahrt - second sub-row)
        row3b: {
          dateYear: { x: 22, y: 476 - (rowSpacing * 2) },
          timeEnd: { x: 90, y: 476 - (rowSpacing * 2) },
          vehicleVin: { x: 237, y: 492 - (rowSpacing * 2) },
          ueber: { x: 436, y: 504 - (rowSpacing * 2) },
          endziel: { x: 365, y: 474 - (rowSpacing * 2) },
          fahrerAdresse: { x: 595, y: 473 - (rowSpacing * 2) },
        },
        // Row 4 (fourth fahrt - first sub-row)
        row4: {
          dateDayMonth: { x: 22, y: 504 - (rowSpacing * 3) },
          timeStart: { x: 90, y: 504 - (rowSpacing * 3) },
          vehicleClass: { x: 142, y: 504 - (rowSpacing * 3) },
          vehicleMake: { x: 142, y: 475 - (rowSpacing * 3) },
          startziel: { x: 365, y: 504 - (rowSpacing * 3) },
          fahrtzweck: { x: 500, y: 504 - (rowSpacing * 3) },
          fahrerName: { x: 595, y: 503 - (rowSpacing * 3) },
        },
        // Row 4b (fourth fahrt - second sub-row)
        row4b: {
          dateYear: { x: 22, y: 476 - (rowSpacing * 3) },
          timeEnd: { x: 90, y: 476 - (rowSpacing * 3) },
          vehicleVin: { x: 237, y: 492 - (rowSpacing * 3) },
          ueber: { x: 436, y: 504 - (rowSpacing * 3) },
          endziel: { x: 365, y: 474 - (rowSpacing * 3) },
          fahrerAdresse: { x: 595, y: 473 - (rowSpacing * 3) },
        },
        // Row 5 (fifth fahrt - first sub-row)
        row5: {
          dateDayMonth: { x: 22, y: 504 - (rowSpacing * 4) },
          timeStart: { x: 90, y: 504 - (rowSpacing * 4) },
          vehicleClass: { x: 142, y: 504 - (rowSpacing * 4) },
          vehicleMake: { x: 142, y: 475 - (rowSpacing * 4) },
          startziel: { x: 365, y: 504 - (rowSpacing * 4) },
          fahrtzweck: { x: 500, y: 504 - (rowSpacing * 4) },
          fahrerName: { x: 595, y: 503 - (rowSpacing * 4) },
        },
        // Row 5b (fifth fahrt - second sub-row)
        row5b: {
          dateYear: { x: 22, y: 476 - (rowSpacing * 4) },
          timeEnd: { x: 90, y: 476 - (rowSpacing * 4) },
          vehicleVin: { x: 237, y: 492 - (rowSpacing * 4) },
          ueber: { x: 436, y: 504 - (rowSpacing * 4) },
          endziel: { x: 365, y: 474 - (rowSpacing * 4) },
          fahrerAdresse: { x: 595, y: 473 - (rowSpacing * 4) },
        },
        // Row 6 (sixth fahrt - first sub-row)
        row6: {
          dateDayMonth: { x: 22, y: 504 - (rowSpacing * 5) },
          timeStart: { x: 90, y: 504 - (rowSpacing * 5) },
          vehicleClass: { x: 142, y: 504 - (rowSpacing * 5) },
          vehicleMake: { x: 142, y: 475 - (rowSpacing * 5) },
          startziel: { x: 365, y: 504 - (rowSpacing * 5) },
          fahrtzweck: { x: 500, y: 504 - (rowSpacing * 5) },
          fahrerName: { x: 595, y: 503 - (rowSpacing * 5) },
        },
        // Row 6b (sixth fahrt - second sub-row)
        row6b: {
          dateYear: { x: 22, y: 476 - (rowSpacing * 5) },
          timeEnd: { x: 90, y: 476 - (rowSpacing * 5) },
          vehicleVin: { x: 237, y: 492 - (rowSpacing * 5) },
          ueber: { x: 436, y: 504 - (rowSpacing * 5) },
          endziel: { x: 365, y: 474 - (rowSpacing * 5) },
          fahrerAdresse: { x: 595, y: 473 - (rowSpacing * 5) },
        },
        // Row 7 (seventh fahrt - first sub-row)
        row7: {
          dateDayMonth: { x: 22, y: 504 - (rowSpacing * 6) },
          timeStart: { x: 90, y: 504 - (rowSpacing * 6) },
          vehicleClass: { x: 142, y: 504 - (rowSpacing * 6) },
          vehicleMake: { x: 142, y: 475 - (rowSpacing * 6) },
          startziel: { x: 365, y: 504 - (rowSpacing * 6) },
          fahrtzweck: { x: 500, y: 504 - (rowSpacing * 6) },
          fahrerName: { x: 595, y: 503 - (rowSpacing * 6) },
        },
        // Row 7b (seventh fahrt - second sub-row)
        row7b: {
          dateYear: { x: 22, y: 476 - (rowSpacing * 6) },
          timeEnd: { x: 90, y: 476 - (rowSpacing * 6) },
          vehicleVin: { x: 237, y: 492 - (rowSpacing * 6) },
          ueber: { x: 436, y: 504 - (rowSpacing * 6) },
          endziel: { x: 365, y: 474 - (rowSpacing * 6) },
          fahrerAdresse: { x: 595, y: 473 - (rowSpacing * 6) },
        },
        // Row 8 (eighth fahrt - first sub-row)
        row8: {
          dateDayMonth: { x: 22, y: 504 - (rowSpacing * 7) },
          timeStart: { x: 90, y: 504 - (rowSpacing * 7) },
          vehicleClass: { x: 142, y: 504 - (rowSpacing * 7) },
          vehicleMake: { x: 142, y: 475 - (rowSpacing * 7) },
          startziel: { x: 365, y: 504 - (rowSpacing * 7) },
          fahrtzweck: { x: 500, y: 504 - (rowSpacing * 7) },
          fahrerName: { x: 595, y: 503 - (rowSpacing * 7) },
        },
        // Row 8b (eighth fahrt - second sub-row)
        row8b: {
          dateYear: { x: 22, y: 476 - (rowSpacing * 7) },
          timeEnd: { x: 90, y: 476 - (rowSpacing * 7) },
          vehicleVin: { x: 237, y: 492 - (rowSpacing * 7) },
          ueber: { x: 436, y: 504 - (rowSpacing * 7) },
          endziel: { x: 365, y: 474 - (rowSpacing * 7) },
          fahrerAdresse: { x: 595, y: 473 - (rowSpacing * 7) },
        },
      };

      // Add license plate suffix to the PDF title
      if (options.licensePlate) {
        firstPage.drawText(options.licensePlate, {
          x: licensePlatePosition.x,
          y: licensePlatePosition.y,
          size: 18,
          font: boldFont,
          color: rgb(0, 0, 0),
        });
      }

      // Populate data for each fahrt
      fahrten.slice(0, 8).forEach((fahrt, index) => {
        // Get the row positions for this fahrt
        const rowKey = `row${index + 1}`;
        const rowPositions = fieldPositions[rowKey];
        
        if (!rowPositions) {
          console.warn(`No positions defined for row ${index + 1}`);
          return;
        }

        // Populate the data with better text handling
        if (rowPositions.dateDayMonth) {
          const dayMonth = formatDayMonth(fahrt.fahrt_datum);
          if (dayMonth) {
            firstPage.drawText(dayMonth, {
              x: rowPositions.dateDayMonth.x,
              y: rowPositions.dateDayMonth.y,
              size: 12,
              font: font,
            });
          }
        }


        if (rowPositions.timeStart) {
          firstPage.drawText(truncateText(fahrt.fahrt_uhrzeit_beginn, 5), {
            x: rowPositions.timeStart.x,
            y: rowPositions.timeStart.y,
            size: 12,
            font: font,
          });
        }

        if (rowPositions.timeEnd) {
          firstPage.drawText(truncateText(fahrt.fahrt_uhrzeit_ende, 5), {
            x: rowPositions.timeEnd.x,
            y: rowPositions.timeEnd.y,
            size: 12,
            font: font,
          });
        }

        if (rowPositions.vehicleMake) {
          firstPage.drawText(truncateText(`${fahrt.vehicle_make}`, 12), {
            x: rowPositions.vehicleMake.x,
            y: rowPositions.vehicleMake.y,
            size: 12,
            font: font,
          });
        }

        if (rowPositions.vehicleClass) {
          firstPage.drawText(truncateText(`${fahrt.vehicle_model}`, 12), {
            x: rowPositions.vehicleClass.x,
            y: rowPositions.vehicleClass.y,
            size: 12,
            font: font,
          });
        }

        if (rowPositions.vehicleVin) {
          firstPage.drawText(truncateText(fahrt.vin, 17), {
            x: rowPositions.vehicleVin.x,
            y: rowPositions.vehicleVin.y,
            size: 10,
            font: font,
          });
        }

        if (rowPositions.startziel) {
          firstPage.drawText(truncateText(fahrt.startziel, 12), {
            x: rowPositions.startziel.x,
            y: rowPositions.startziel.y,
            size: 12,
            font: font,
          });
        }

        if (rowPositions.ueber) {
          firstPage.drawText(truncateText(fahrt.ueber, 10), {
            x: rowPositions.ueber.x,
            y: rowPositions.ueber.y,
            size: 12,
            font: font,
          });
        }

        if (rowPositions.fahrtzweck) {
          const fahrtzweckText = getFahrtzweckLabel(fahrt.fahrtzweck);
          
          // Split long text into multiple lines (up to 3 lines)
          if (fahrtzweckText.length > 20) {
            const words = fahrtzweckText.split(' ');
            const lines = [];
            let currentLine = '';
            
            // Distribute words across lines
            for (let i = 0; i < words.length; i++) {
              const testLine = currentLine + (currentLine ? ' ' : '') + words[i];
              
              // If adding this word would make the line too long, start a new line
              if (testLine.length > 16 && currentLine) {
                lines.push(currentLine);
                currentLine = words[i];
              } else {
                currentLine = testLine;
              }
            }
            
            // Add the last line if it has content
            if (currentLine) {
              lines.push(currentLine);
            }
            
            // Draw up to 3 lines
            const maxLines = Math.min(lines.length, 3);
            for (let i = 0; i < maxLines; i++) {
              firstPage.drawText(lines[i], {
                x: rowPositions.fahrtzweck.x,
                y: rowPositions.fahrtzweck.y - (i * 12),
                size: 10,
                font: font,
              });
            }
          } else {
            // Single line for shorter text
            firstPage.drawText(fahrtzweckText, {
              x: rowPositions.fahrtzweck.x,
              y: rowPositions.fahrtzweck.y,
              size: 10,
              font: font,
            });
          }
        }

        if (rowPositions.fahrerName) {
          firstPage.drawText(truncateText(fahrt.fahrer_name, 15), {
            x: rowPositions.fahrerName.x,
            y: rowPositions.fahrerName.y,
            size: 12,
            font: font,
          });
        }

        // Handle second row data if it exists
        const rowBKey = `row${index + 1}b`;
        const rowBPositions = fieldPositions[rowBKey];
        
        if (rowBPositions) {
          if (rowBPositions.dateYear) {
            firstPage.drawText(formatYear(fahrt.fahrt_datum), {
              x: rowBPositions.dateYear.x,
              y: rowBPositions.dateYear.y,
              size: 12,
              font: font,
            });
          }

          if (rowBPositions.timeEnd) {
            firstPage.drawText(truncateText(fahrt.fahrt_uhrzeit_ende, 5), {
              x: rowBPositions.timeEnd.x,
              y: rowBPositions.timeEnd.y,
              size: 12,
              font: font,
            });
          }

          if (rowBPositions.vehicleVin) {
            firstPage.drawText(truncateText(fahrt.vin, 17), {
              x: rowBPositions.vehicleVin.x,
              y: rowBPositions.vehicleVin.y,
              size: 10,
              font: font,
            });
          }

          if (rowBPositions.ueber) {
            firstPage.drawText(truncateText(fahrt.ueber, 10), {
              x: rowBPositions.ueber.x,
              y: rowBPositions.ueber.y,
              size: 12,
              font: font,
            });
          }

          if (rowBPositions.endziel) {
            firstPage.drawText(truncateText(fahrt.endziel, 12), {
              x: rowBPositions.endziel.x,
              y: rowBPositions.endziel.y,
              size: 12,
              font: font,
            });
          }

          if (rowBPositions.fahrerAdresse) {
            firstPage.drawText(truncateText(fahrt.fahrer_adresse, 50), {
              x: rowBPositions.fahrerAdresse.x,
              y: rowBPositions.fahrerAdresse.y,
              size: 12,
              font: font,
            });
          }
        }
      });

      return pdfDoc;
    } catch (error) {
      console.error('Error populating PDF template:', error);
      throw error;
    }
  }

  // Generate and download the populated PDF
  async generatePDF(fahrten, options = {}) {
    try {
      const pdfDoc = await this.populateTemplate(fahrten, options);
      const pdfBytes = await pdfDoc.save();
      
      // Create blob and download
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `Fahrtennachweis_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  // Test method to generate PDF with sample data for coordinate testing
  async generateTestPDF() {
    const testFahrten = [
      {
        fahrt_datum: '2024-01-15',
        fahrt_uhrzeit_beginn: '08:00',
        fahrt_uhrzeit_ende: '10:30',
        vehicle_make: 'BMW',
        vehicle_model: '320i',
        vin: 'WBAFR7C50BC123456',
        startziel: 'München',
        ueber: 'A8',
        endziel: 'Stuttgart',
        fahrtzweck: 'Innen-aussenreinigung',
        fahrer_name: 'Max Mustermann',
        fahrer_adresse: '12345 München, Hauptstraße 1'
      },
      {
        fahrt_datum: '2024-01-16',
        fahrt_uhrzeit_beginn: '14:00',
        fahrt_uhrzeit_ende: '16:45',
        vehicle_make: 'Audi',
        vehicle_model: 'A4',
        vin: 'WAUZZZ8V9KA123456',
        startziel: 'Stuttgart',
        ueber: 'A81',
        endziel: 'Heilbronn',
        fahrtzweck: 'Zurück gefahren fertig mit Sauber machen',
        fahrer_name: 'Anna Schmidt',
        fahrer_adresse: '74072 Heilbronn, Bahnhofstraße 5'
      }
    ];

    try {
      await this.generatePDF(testFahrten);
    } catch (error) {
      console.error('Error generating test PDF:', error);
      throw error;
    }
  }
}

export const pdfTemplateService = new PDFTemplateService();
